import { randomBytes } from 'node:crypto'
import { createServerFn } from '@tanstack/react-start'
import { z } from 'zod'
import {
  getManualFfOrderStore,
  insertManualFfOrderStore,
  listManualFfOrdersStore,
  updateManualFfOrderStore,
  type ManualFfOrder,
  type ManualFfOrderStatus,
} from '#bv/lib/manualFfStore'
import { getServiceSupabase } from '#bv/lib/supabaseAdminServer'
import { assertProductsInStockOrThrow } from '#bv/lib/productStock'
import { parseBuyerEmailFromCheckoutPayload } from '#bv/lib/buyerEmail'

function getPaypalFfEmail(): string {
  return process.env.PAYPAL_FF_EMAIL?.trim() || ''
}

function nowIso(): string {
  return new Date().toISOString()
}

function normalizeMoney(value: number): number {
  return Math.max(0, Math.round(value * 100) / 100)
}

function createOrderId() {
  return `ff_${randomBytes(6).toString('hex')}`
}

function createOrderNoteId() {
  return `c${randomBytes(10).toString('hex')}`
}

const orderItemSchema = z.object({
  productId: z.number().int().positive(),
  name: z.string().min(1).max(255),
  unitPrice: z.number().nonnegative(),
  quantity: z.number().int().positive().max(100),
  robuxAmount: z.number().int().positive().optional(),
  robuxGamepassPrice: z.number().int().positive().optional(),
  robuxGamepassLink: z.string().trim().min(12).max(512).optional(),
  serverBoostCount: z.number().int().positive().optional(),
  serverBoostInviteLink: z.string().trim().min(12).max(512).optional(),
})

const createManualOrderSchema = z.object({
  email: z.string(),
  items: z.array(orderItemSchema).min(1).max(50),
})

export const getManualFfEnabled = createServerFn({ method: 'GET' }).handler(() => {
  return !!getPaypalFfEmail()
})

export const getManualFfStorageBackend = createServerFn({ method: 'GET' }).handler(() => {
  return getServiceSupabase() ? 'supabase' : 'file'
})

export const createManualFfOrder = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => createManualOrderSchema.parse(d))
  .handler(async ({ data }) => {
    const email = parseBuyerEmailFromCheckoutPayload(data.email)
    const paypalEmail = getPaypalFfEmail()
    if (!paypalEmail) {
      throw new Error('Manual PayPal F&F is not configured (set PAYPAL_FF_EMAIL).')
    }
    await assertProductsInStockOrThrow(
      data.items.map((item) => ({ productId: item.productId, name: item.name })),
    )
    const { getMergedProductById } = await import('#bv/lib/productCatalog')
    const { assertSoloCustomCheckoutLine } = await import('#bv/lib/customQuantityCheckout')
    assertSoloCustomCheckoutLine(data.items, 'Robux, Server Boost, and growth packages')
    const { validateRobuxCartLine } = await import('#bv/lib/robuxProduct')
    const robuxLines = data.items.filter((i) => i.robuxAmount != null)
    if (robuxLines.length > 0) {
      const line = robuxLines[0]!
      const product = await getMergedProductById(line.productId)
      const err = validateRobuxCartLine(line, product)
      if (err) throw new Error(err)
    }
    const { validateServerBoostCartLine } = await import('#bv/lib/serverBoostProduct')
    const boostLines = data.items.filter((i) => i.serverBoostCount != null)
    if (boostLines.length > 0) {
      const line = boostLines[0]!
      const product = await getMergedProductById(line.productId)
      const err = validateServerBoostCartLine(line, product)
      if (err) throw new Error(err)
    }
    const totalUsd = normalizeMoney(
      data.items.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0),
    )
    const createdAt = nowIso()
    const order: ManualFfOrder = {
      id: createOrderId(),
      buyerEmail: email,
      paypalEmail,
      currency: 'USD',
      totalUsd,
      noteId: createOrderNoteId(),
      status: 'awaiting_payment',
      items: data.items,
      timeline: [
        { status: 'awaiting_payment', at: createdAt, note: 'Order created. Awaiting buyer payment.' },
      ],
      createdAt,
      updatedAt: createdAt,
    }
    await insertManualFfOrderStore(order)
    try {
      const { sendManualFfDiscordUpdate } = await import('#bv/lib/manualFfDiscord')
      await sendManualFfDiscordUpdate(order, 'Order Created')
    } catch {
      // Do not block checkout if Discord logging fails.
    }
    return {
      orderId: order.id,
      noteId: order.noteId,
      paypalEmail: order.paypalEmail,
      totalUsd: order.totalUsd,
      currency: order.currency,
    }
  })

const getOrderSchema = z.object({
  orderId: z.string().min(5),
})

export const getManualFfOrder = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => getOrderSchema.parse(d))
  .handler(async ({ data }) => {
    const order = await getManualFfOrderStore(data.orderId)
    if (!order) throw new Error('Order not found')
    return order
  })

const markPaymentSentSchema = z.object({
  orderId: z.string().min(5),
})

export const markManualFfPaymentSent = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => markPaymentSentSchema.parse(d))
  .handler(async ({ data }) => {
    const order = await getManualFfOrderStore(data.orderId)
    if (!order) throw new Error('Order not found')
    if (order.status === 'awaiting_payment') {
      const updatedAt = nowIso()
      order.status = 'verifying_payment'
      order.updatedAt = updatedAt
      order.timeline.push({
        status: 'verifying_payment',
        at: updatedAt,
        note: 'Buyer marked payment as sent. Waiting for seller verification.',
      })
      await updateManualFfOrderStore(order)
    }
    try {
      const { sendManualFfDiscordUpdate } = await import('#bv/lib/manualFfDiscord')
      await sendManualFfDiscordUpdate(order, 'Buyer Clicked Payment Sent')
    } catch {
      // Do not block buyer status update if Discord logging fails.
    }
    return order
  })

const setStatusSchema = z.object({
  orderId: z.string().min(5),
  status: z.enum(['verifying_payment', 'paid', 'delivered', 'cancelled']),
  note: z.string().trim().max(300).optional(),
})

export async function setManualFfOrderStatus(input: z.infer<typeof setStatusSchema>): Promise<ManualFfOrder> {
  return setManualFfOrderStatusWithOptions(input, { notifyDiscord: true })
}

export async function setManualFfOrderStatusWithOptions(
  input: z.infer<typeof setStatusSchema>,
  options: { notifyDiscord: boolean },
): Promise<ManualFfOrder> {
  const parsed = setStatusSchema.parse(input)
  const order = await getManualFfOrderStore(parsed.orderId)
  if (!order) throw new Error('Order not found')
  const updatedAt = nowIso()
  order.status = parsed.status as ManualFfOrderStatus
  order.updatedAt = updatedAt
  order.timeline.push({
    status: parsed.status,
    at: updatedAt,
    note: parsed.note?.trim() || undefined,
  })
  await updateManualFfOrderStore(order)
  if (options.notifyDiscord) {
    try {
      const { sendManualFfDiscordUpdate } = await import('#bv/lib/manualFfDiscord')
      await sendManualFfDiscordUpdate(order, `Seller set status to ${parsed.status}`)
    } catch {
      // Do not block status updates if Discord logging fails.
    }
  }
  return order
}

export async function listManualFfOrders(): Promise<ManualFfOrder[]> {
  return listManualFfOrdersStore()
}

export async function markManualFfOrderRefunded(orderId: string, note?: string): Promise<ManualFfOrder> {
  const order = await getManualFfOrderStore(orderId)
  if (!order) throw new Error('Order not found.')
  if (order.status === 'refunded') return order
  if (order.status !== 'paid' && order.status !== 'delivered') {
    throw new Error('Only paid or delivered F&F orders can be marked refunded.')
  }
  const updatedAt = nowIso()
  order.status = 'refunded'
  order.updatedAt = updatedAt
  order.timeline.push({
    status: 'refunded',
    at: updatedAt,
    note: note?.trim() || 'Marked refunded in admin.',
  })
  await updateManualFfOrderStore(order)
  try {
    const { sendManualFfDiscordUpdate } = await import('#bv/lib/manualFfDiscord')
    await sendManualFfDiscordUpdate(order, 'Order refunded (admin)')
  } catch {
    // Do not block refund if Discord logging fails.
  }
  return order
}
